import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import QuizRoundedIcon from '@mui/icons-material/QuizRounded';
import StudentLayout from './components/StudentLayout';
import { AuthContext } from '../auth/AuthContext';
import { listSubjects } from '../api/subjects';
import { listLessons } from '../api/lessons';
import { listLessonProgress, upsertLessonProgress } from '../api/lessonProgress';
import { createSubmission, listSubmissions } from '../api/submissions';
import { listAssessments } from '../api/assessments';
import { listRecordings } from '../api/recordings';

const Subjects = () => {
  const { user } = useContext(AuthContext) ?? {};
  const [subjects, setSubjects] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTab, setSelectedTab] = useState('lessons');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openQuizzes, setOpenQuizzes] = useState({});
  const [quizAnswers, setQuizAnswers] = useState({});
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [lessonProgress, setLessonProgress] = useState({});

  const grade = user?.grade;
  const currentSubject = useMemo(
    () => subjects.find((s) => s._id === selectedSubject),
    [subjects, selectedSubject]
  );

  const persistProgress = async (lessonId, status) => {
    try {
      await upsertLessonProgress({ lessonId, status });
    } catch (err) {
      // ignore API errors for now
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const subj = await listSubjects(grade ? { grade } : {});
        setSubjects(subj || []);
        const first = subj?.[0]?._id || '';
        setSelectedSubject(first);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load subjects');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [grade]);

  useEffect(() => {
    const loadContent = async () => {
      if (!selectedSubject) return;
      try {
        setLoading(true);
        const [lessonData, assessmentData, submissionData] = await Promise.all([
          listLessons({ subjectId: selectedSubject, grade, availableOnly: true }),
          listAssessments({ subjectId: selectedSubject, grade }),
          listSubmissions({}),
        ]);
        setLessons(Array.isArray(lessonData) ? lessonData : []);
        setAssessments(Array.isArray(assessmentData) ? assessmentData : []);
        const submissionMap = {};
        (submissionData || []).forEach((sub) => { submissionMap[sub.assessmentId] = sub; });
        setSubmissions(submissionMap);

        const progress = await listLessonProgress({});
        const progressMap = {};
        (progress || []).forEach((row) => {
          if (!row.lessonId) return;
          progressMap[row.lessonId] = row.status || 'viewed';
        });
        setLessonProgress(progressMap);

        if (currentSubject?.name) {
          const recData = await listRecordings({ subject: currentSubject.name, pageSize: 50 });
          setRecordings(Array.isArray(recData?.items) ? recData.items : []);
        } else {
          setRecordings([]);
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load content');
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, [selectedSubject, grade, currentSubject?.name]);

  const getYouTubeEmbed = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/i
    );
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const handleSubmitAssessment = async (assessmentId, files) => {
    if (!files?.length) return;
    setUploading(true);
    setError(null);
    try {
      await createSubmission({ assessmentId, files });
      const submissionData = await listSubmissions({});
      const submissionMap = {};
      (submissionData || []).forEach((sub) => { submissionMap[sub.assessmentId] = sub; });
      setSubmissions(submissionMap);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to submit assessment');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitQuizAssessment = async (assessmentId) => {
    const answers = assessmentAnswers[assessmentId] || {};
    setUploading(true);
    setError(null);
    try {
      await createSubmission({ assessmentId, answers });
      const submissionData = await listSubmissions({});
      const submissionMap = {};
      (submissionData || []).forEach((sub) => { submissionMap[sub.assessmentId] = sub; });
      setSubmissions(submissionMap);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to submit quiz');
    } finally {
      setUploading(false);
    }
  };

  const toggleLessonComplete = (lessonId) => {
    if (!lessonId || !selectedSubject) return;
    setLessonProgress((prev) => {
      const currentlyCompleted = prev[lessonId] === 'completed';
      const nextStatus = currentlyCompleted ? 'viewed' : 'completed';
      const next = { ...prev, [lessonId]: nextStatus };
      persistProgress(lessonId, nextStatus);
      return next;
    });
  };

  const markLessonViewed = (lessonId) => {
    if (!lessonId || lessonProgress[lessonId] === 'completed') return;
    setLessonProgress((prev) => {
      const next = { ...prev, [lessonId]: 'viewed' };
      persistProgress(lessonId, 'viewed');
      return next;
    });
  };

  const toggleQuiz = (lessonId) => {
    setOpenQuizzes((prev) => ({ ...prev, [lessonId]: !prev[lessonId] }));
  };

  const handleQuizAnswer = (lessonId, qIndex, value) => {
    setQuizAnswers((prev) => ({
      ...prev,
      [lessonId]: {
        ...(prev[lessonId] || {}),
        [qIndex]: value,
      },
    }));
  };

  const handleAssessmentAnswer = (assessmentId, qIndex, value) => {
    setAssessmentAnswers((prev) => ({
      ...prev,
      [assessmentId]: {
        ...(prev[assessmentId] || {}),
        [qIndex]: value,
      },
    }));
  };

  const subjectCompletion = useMemo(() => {
    const completed = lessons.filter((lesson) => lessonProgress[lesson._id] === 'completed').length;
    const total = lessons.length || 0;
    const pct = total ? Math.round((completed / total) * 100) : 0;
    return { completed, total, pct };
  }, [lessonProgress, lessons, selectedSubject]);

  return (
    <StudentLayout>
      <Box>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary.main">
              Subject Room
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Watch lessons, download notes, and submit assessments for your subjects.
            </Typography>
          </Box>

          {(loading || uploading) && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="Subject"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              {subjects.map((s) => (
                <MenuItem key={s._id} value={s._id}>
 {s.name} {s.track ? `(${s.track})` : ''}
                </MenuItem>
              ))}
            </TextField>
 <Chip label={currentSubject ? `Grade ${currentSubject.grade}` : `Grade ${grade || ''}`} />
          </Stack>

          <Card variant="outlined">
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>
                  Progress
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={subjectCompletion.pct}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Chip label={`${subjectCompletion.completed}/${subjectCompletion.total} lessons`} />
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader
              title={currentSubject?.name || 'Lessons & assessments'}
              subheader="Access published lessons and due assessments"
            />
            <CardContent>
              <Tabs
                value={selectedTab}
                onChange={(_e, value) => setSelectedTab(value)}
                sx={{ mb: 2 }}
              >
                <Tab label="Lessons" value="lessons" />
                <Tab label="Assessments" value="assessments" />
                <Tab label="Replays" value="recordings" />
              </Tabs>

              {selectedTab === 'lessons' && (
                <Stack spacing={2}>
                  {lessons.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No lessons yet for this subject.
                    </Typography>
                  )}
                  {lessons.map((lesson) => (
                    <Card key={lesson._id} variant="outlined">
                      <CardContent>
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontWeight={700}>{lesson.title}</Typography>
                            <Chip size="small" label={`G${lesson.grade}`} />
                            {(lesson.videoUrl || lesson.videoFile?.url) && (
                              <Chip size="small" color="primary" label="Video" />
                            )}
                            <Chip
                              size="small"
 color={lesson.isVisible ? 'success' : 'default'}
 label={lesson.isVisible ? 'Live' : 'Hidden'}
                            />
                            {lesson.quiz?.type === 'embedded' && (
                              <Chip size="small" color="secondary" label="Quiz" icon={<QuizRoundedIcon />} />
                            )}
                            {lessonProgress[lesson._id] === 'completed' && (
                              <Chip
                                size="small"
                                color="success"
                                label="Completed"
                                icon={<CheckCircleRoundedIcon />}
                              />
                            )}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {lesson.description || 'No description provided.'}
                          </Typography>
                          {lesson.educatorFeedback && (
                            <Alert severity="info">
                              Educator feedback: {lesson.educatorFeedback}
                            </Alert>
                          )}
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {lesson.videoUrl && (
                              <Button
                                startIcon={<PlayCircleFilledRoundedIcon />}
                                size="small"
                                href={lesson.videoUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => markLessonViewed(lesson._id)}
                              >
                                Watch
                              </Button>
                            )}
                            {lesson.quiz?.type === 'embedded' && (
                              <Button
                                startIcon={<QuizRoundedIcon />}
                                size="small"
                                onClick={() => {
                                  markLessonViewed(lesson._id);
                                  toggleQuiz(lesson._id);
                                }}
                              >
 {openQuizzes[lesson._id] ? 'Hide Quiz' : 'Start Quiz'}
                              </Button>
                            )}
                            {(lesson.files || []).map((file) => (
                              <Button
                                key={file.key}
                                startIcon={<DownloadRoundedIcon />}
                                size="small"
                                href={file.url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => markLessonViewed(lesson._id)}
                              >
                                {file.originalName}
                              </Button>
                            ))}
                            <Button
                              size="small"
 variant={lessonProgress[lesson._id] === 'completed' ? 'contained' : 'outlined'}
 color={lessonProgress[lesson._id] === 'completed' ? 'success' : 'primary'}
                              onClick={() => toggleLessonComplete(lesson._id)}
                              startIcon={<CheckCircleRoundedIcon />}
                            >
 {lessonProgress[lesson._id] === 'completed' ? 'Completed' : 'Mark complete'}
                            </Button>
                          </Stack>
                          {lesson.videoFile?.url && (
                            <Box sx={{ mt: 1 }}>
                              <video
                                controls
                                width="100%"
                                src={lesson.videoFile.url}
                                onPlay={() => markLessonViewed(lesson._id)}
                              />
                            </Box>
                          )}
                          {lesson.videoUrl && getYouTubeEmbed(lesson.videoUrl) && (
                            <Box sx={{ mt: 1, position: 'relative', paddingTop: '56.25%' }}>
                              <Box
                                component="iframe"
                                src={getYouTubeEmbed(lesson.videoUrl)}
                                title={`${lesson.title}-video`}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  border: 0,
                                }}
                              />
                            </Box>
                          )}
                          {lesson.videoUrl && !getYouTubeEmbed(lesson.videoUrl) && (
                            <Box sx={{ mt: 1 }}>
                              <video
                                controls
                                width="100%"
                                src={lesson.videoUrl}
                                onPlay={() => markLessonViewed(lesson._id)}
                              />
                            </Box>
                          )}
                          {lesson.quiz?.type === 'embedded' &&
                            Array.isArray(lesson.quiz?.config?.questions) &&
                            lesson.quiz.config.questions.length > 0 &&
                            openQuizzes[lesson._id] && (
                              <Stack spacing={2} sx={{ mt: 1.5 }}>
                                {lesson.quiz.config.questions.map((question, idx) => (
                                  <Card key={`${lesson._id}-q-${idx}`} variant="outlined">
                                    <CardContent>
                                      <Stack spacing={1}>
                                        <Typography fontWeight={600}>
                                          {idx + 1}. {question.prompt}
                                        </Typography>
                                        {question.type === 'short' ? (
                                          <TextField
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            placeholder="Type your answer"
                                            value={quizAnswers[lesson._id]?.[idx] || ''}
                                            onChange={(e) => handleQuizAnswer(lesson._id, idx, e.target.value)}
                                          />
 ) : (
                                          <FormControl>
                                            <FormLabel>Choose one</FormLabel>
                                            <RadioGroup
                                              value={quizAnswers[lesson._id]?.[idx] ?? ''}
                                              onChange={(e) => handleQuizAnswer(lesson._id, idx, e.target.value)}
                                            >
                                              {(question.options || []).map((opt, optIdx) => (
                                                <FormControlLabel
                                                  key={`${lesson._id}-q-${idx}-opt-${optIdx}`}
                                                  value={String(optIdx)}
                                                  control={<Radio />}
                                                  label={opt}
                                                />
                                              ))}
                                            </RadioGroup>
                                          </FormControl>
                                        )}
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                ))}
                              </Stack>
                            )}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}

              {selectedTab === 'recordings' && (
                <Stack spacing={2}>
                  {recordings.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No replays yet for this subject.
                    </Typography>
 ) : (
                    recordings.map((rec) => (
                      <Card key={rec._id} variant="outlined">
                        <CardContent>
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography fontWeight={700}>{rec.title || rec.subject || 'Session'}</Typography>
                              <Chip size="small" label={rec.subject || 'Subject'} />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
 {rec.startTime ? new Date(rec.startTime).toLocaleString() : 'Recorded session'}
                            </Typography>
                            {Array.isArray(rec.recordingChapters) && rec.recordingChapters.length > 0 && (
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                {rec.recordingChapters.map((chapter, idx) => (
                                  <Button
                                    key={`${rec._id}-ch-${idx}`}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      if (!rec.recordingUrl) return;
                                      const url = `${rec.recordingUrl}?t=${chapter.startSeconds || 0}`;
                                      window.open(url, '_blank', 'noopener,noreferrer');
                                    }}
                                  >
                                    {chapter.label}
                                  </Button>
                                ))}
                              </Stack>
                            )}
                            <Button
                              startIcon={<PlayCircleFilledRoundedIcon />}
                              size="small"
                              variant="contained"
                              disabled={!rec.recordingUrl}
                              onClick={() => rec.recordingUrl && window.open(rec.recordingUrl, '_blank', 'noopener,noreferrer')}
                            >
                              Watch replay
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Stack>
              )}

              {selectedTab === 'assessments' && (
                <Stack spacing={2}>
                  {assessments.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No assessments assigned for this subject.
                    </Typography>
                  )}
                  {assessments.map((assessment) => {
                    const existing = submissions[assessment._id];
                    const isReleased = assessment.release?.released;
                    return (
                      <Card key={assessment._id} variant="outlined">
                        <CardContent>
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography fontWeight={700}>{assessment.title}</Typography>
                              <Chip size="small" label={assessment.type?.toUpperCase()} />
                              <Chip size="small" label={`Marks: ${assessment.totalMarks || 0}`} />
                              <Chip
                                size="small"
                                color="secondary"
                                label={
                                  assessment.dueDate
                                    ? `Due ${new Date(assessment.dueDate).toLocaleString()}`
                                    : 'Due date TBC'
                                }
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {assessment.description || 'No description provided.'}
                            </Typography>
                            <Stack direction="row" spacing={1} divider={<Divider flexItem orientation="vertical" />}>
                              <Typography variant="caption">
                                Open: {assessment.openDate ? new Date(assessment.openDate).toLocaleString() : 'Now'}
                              </Typography>
                              <Typography variant="caption">
                                Close: {assessment.closeDate ? new Date(assessment.closeDate).toLocaleString() : 'Due'}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              {assessment.attachments?.map((file) => (
                                <Button
                                  key={file.key}
                                  startIcon={<DownloadRoundedIcon />}
                                  size="small"
                                  href={file.url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {file.originalName}
                                </Button>
                              ))}
                              {assessment.memo?.length > 0 && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={isReleased ? 'Memo available' : 'Memo available (after release)'}
                                />
                              )}
                            </Stack>
                            {existing ? (
                              <Alert severity={isReleased ? 'success' : 'info'}>
                                Submitted. Status: {existing.status}.
                                {isReleased && existing.educatorScore != null && ` Score: ${existing.educatorScore}/${existing.totalMarks || assessment.totalMarks || ''}.`}
                                {isReleased && existing.feedback ? ` Feedback: ${existing.feedback}` : ''}
                                {isReleased && existing.feedbackFiles?.length ? (
                                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    {existing.feedbackFiles.map((file) => (
                                      <Button
                                        key={file.key}
                                        size="small"
                                        variant="outlined"
                                        href={file.url}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        Feedback: {file.originalName}
                                      </Button>
                                    ))}
                                  </Stack>
                                ) : null}
                                {!isReleased && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Marks will appear after moderator release.
                                  </Typography>
                                )}
                              </Alert>
                            ) : (
                              <>
                                {Array.isArray(assessment.quiz?.config?.questions) &&
                                assessment.quiz.config.questions.length > 0 ? (
                                  <Stack spacing={2}>
                                    {assessment.quiz.config.questions.map((question, idx) => (
                                      <Card key={`${assessment._id}-q-${idx}`} variant="outlined">
                                        <CardContent>
                                          <Stack spacing={1}>
                                            <Typography fontWeight={600}>
                                              {idx + 1}. {question.prompt}
                                            </Typography>
                                            {question.type === 'short' ? (
                                              <TextField
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                placeholder="Type your answer"
                                                value={assessmentAnswers[assessment._id]?.[idx] || ''}
                                                onChange={(e) => handleAssessmentAnswer(assessment._id, idx, e.target.value)}
                                              />
                                            ) : (
                                              <FormControl>
                                                <FormLabel>Choose one</FormLabel>
                                                <RadioGroup
                                                  value={assessmentAnswers[assessment._id]?.[idx] ?? ''}
                                                  onChange={(e) => handleAssessmentAnswer(assessment._id, idx, e.target.value)}
                                                >
                                                  {(question.options || []).map((opt, optIdx) => (
                                                    <FormControlLabel
                                                      key={`${assessment._id}-q-${idx}-opt-${optIdx}`}
                                                      value={String(optIdx)}
                                                      control={<Radio />}
                                                      label={opt}
                                                    />
                                                  ))}
                                                </RadioGroup>
                                              </FormControl>
                                            )}
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    ))}
                                    <Button
                                      variant="contained"
                                      startIcon={<AssignmentTurnedInRoundedIcon />}
                                      onClick={() => handleSubmitQuizAssessment(assessment._id)}
                                      disabled={uploading}
                                    >
                                      Submit quiz
                                    </Button>
                                  </Stack>
                                ) : (
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                                    <Button
                                      component="label"
                                      variant="outlined"
                                      startIcon={<AssignmentTurnedInRoundedIcon />}
                                      disabled={uploading}
                                    >
                                      Upload submission
                                      <input
                                        hidden
                                        type="file"
                                        multiple
                                        onChange={(e) =>
                                          handleSubmitAssessment(assessment._id, Array.from(e.target.files || []))
                                        }
                                      />
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">
                                      PDF/Word uploads supported
                                    </Typography>
                                  </Stack>
                                )}
                              </>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </StudentLayout>
  );
};

export default Subjects;
